<template>
	<div class="w-full pointer-events-auto h-full">
		<div class="flex h-full w-full flex-col bg-white shadow-xl p-6">
			<div class="flex flex-col h-full">
				<div class="flex items-start justify-between mb-6">
					<h2
						id="slide-over-title"
						class="text-lg capitalize font-medium text-gray-900 py-0"
					>
						{{ $t("Forgot Password") }}
					</h2>
					<div class="ml-3 flex h-7 items-center">
						<button
							type="button"
							class="-m-2 p-2 text-gray-700"
							@click="emits('close')"
						>
							<span class="sr-only">{{ $t("Close panel") }}</span>
							<XMarkIcon class="w-6 h-6" />
						</button>
					</div>
				</div>

				<div class="flex-1 min-h-0">
					<form class="flex-1 min-h-0 flex flex-col gap-6">
						<p>
							{{
								$t("Please enter your email address to recover your password.")
							}}
						</p>
						<div>
							<label
								for="email"
								class="block text-sm font-medium text-gray-700 mb-1"
							>
								{{ $t("Email address") }}
							</label>
							<input
								v-model="passwordRequestEmail"
								type="email"
								name="email"
								class="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:z-10 sm:text-sm"
							/>
							<span
								v-if="$v.email.$error"
								class="text-red-600 text-sm focus:ring-brand-primary border-gray-300 rounded"
							>
								{{
									$t(
										`validation.${
											($v?.email?.$errors[0]?.$params as any).type
										}`,
										$v?.email?.$errors[0]?.$params as any
									)
								}}
							</span>
						</div>
						<div class="pt-4">
							<button
								type="button"
								class="flex capitalize items-center justify-center px-5 py-2 text-base font-medium text-white shadow-sm bg-gray-800 disabled:opacity-50 hover:bg-gray-900 transition ease-in-out duration-200"
								@click="sendPasswordRequest"
							>
								{{ $t("Send Recovery Email") }}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { XMarkIcon } from "@heroicons/vue/24/solid";
import { required, email } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import { computed, ref } from "vue";

const { passwordRequest } = useNorcePwa();
const { executeRecaptcha } = useRecaptcha();
const { $toast } = useNuxtApp();

const emits = defineEmits<{
	(e: "success"): void;
	(e: "close"): void;
}>();
const { t, locale } = useI18n();
const rules = computed(() => ({
	email: {
		required,
		email
	}
}));
const formData = ref({
	email: ""
});

const $v = useVuelidate(rules, formData);

const passwordRequestEmail = ref("");
const passwordRequestResult = ref<string | null>(null);
const passwordRequestError = ref<string | null>(null);

watch(passwordRequestEmail, (val) => {
	formData.value.email = val;
});

async function sendPasswordRequest() {
	passwordRequestResult.value = null;
	passwordRequestError.value = null;
	$v.value.$touch();
	const valid = await $v.value.$validate();
	if (!valid) {
		passwordRequestError.value = t("validation.email");
		return;
	}
	try {
		const recaptchaToken = await executeRecaptcha("password_request");
		const res = await passwordRequest({
			email: passwordRequestEmail.value,
			recaptchaToken,
			localeCode: locale.value
		});
		passwordRequestResult.value = JSON.stringify(res);
		if (res.ok) {
			$toast.success(t("successfulPasswordRequest"));
		} else {
			$toast.error(res.error || t("errorPasswordRequest"));
		}
	} catch (e) {
		passwordRequestError.value = e?.message || "Unknown error";
		if (e?.statusCode === 403) {
			$toast.error(t("recaptcha_failed"));
		} else {
			$toast.error(t(passwordRequestError.value));
		}
	}
}
</script>
